// src/components/Navigation/MobileBottomNav.js
import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';

// Import icons from Material-UI
import InsightsIcon from '@mui/icons-material/Insights';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Import navigation config
import { NAVIGATION_CONFIG } from '../../config/navigationConfig';
import { useCoachingRole } from '../../hooks/useCoachingRole';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Mobile Bottom Navigation Bar
 *
 * Displays a fixed bottom navigation bar on mobile devices with 4 primary actions:
 * - Insights (Dashboard)
 * - Notebook (Notes)
 * - Care
 * - Faana AI (Chat)
 */
function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isCoach } = useCoachingRole();
  const { isPlatformAdmin } = useAuth();

  // Icon mapping - matches approved icons from navigationConfig
  const iconMap = {
    'InsightsIcon': InsightsIcon,
    'SmartToyIcon': SmartToyIcon,
    'AccountTreeIcon': AccountTreeIcon,
    'MenuBookIcon': MenuBookIcon,
    'VolunteerActivismIcon': VolunteerActivismIcon,
    'SportsHandballIcon': SportsHandballIcon,
    'AssessmentIcon': AssessmentIcon,
};

  // Mobile bottom nav: if coach or admin, show coach-home first; otherwise standard order
  const mobileNavOrder = (isCoach || isPlatformAdmin)
    ? ['coach-home', 'care', 'notebook', 'reports', 'chat']
    : ['care', 'notebook', 'reports', 'chat'];
  const mobileNavItems = mobileNavOrder
    .map(id => NAVIGATION_CONFIG.primary.find(item => item.id === id))
    .filter(Boolean);

  // Don't render on non-mobile devices
  if (!isMobile) {
    return null;
  }

  // Get current value based on route
  const getCurrentValue = () => {
    const currentPath = location.pathname;

    // Find matching primary nav item by route
    const matchedItem = NAVIGATION_CONFIG.primary.find(item => {
      if (item.route === currentPath) return true;
      // Handle route aliases (e.g., /client-management for Entities)
      if (item.id === 'entities' && currentPath === '/client-management') return true;
      return false;
    });

    if (matchedItem) return matchedItem.id;
    return (isCoach || isPlatformAdmin) ? 'coach-home' : 'reports';
  };

  const handleChange = (event, newValue) => {
    const navItem = NAVIGATION_CONFIG.primary.find(item => item.id === newValue);
    if (navItem) {
      navigate(navItem.route);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
      }}
      elevation={8}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 4px',
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.65rem',
            '&.Mui-selected': {
              fontSize: '0.65rem',
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: 26,
          },
        }}
      >
        {mobileNavItems.map(item => {
          const IconComponent = iconMap[item.icon];

          return (
            <BottomNavigationAction
              key={item.id}
              label={item.label}
              value={item.id}
              icon={<IconComponent />}
              sx={{
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'secondary.main',
                },
              }}
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
}

export default MobileBottomNav;
